import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {IIngredient} from './ingredient.interface';
import {cloneDeep} from 'lodash';
import {IIngredientCategory} from '../../interfaces/database/database.interface';
import {IMeasurementUnit} from '../measurement';
import {firstValueFrom} from 'rxjs';
import {IIngredientSearch, IIngredientSearchHit} from './ingredient-search.interface';

export interface IRIngredient {
  ingredients: IIngredient[];
  ingredientsSearch: IIngredientSearch;
  selectedId: string;
  count: number;
  ingredientCategories: IIngredientCategory[];
  userListIngredients: IIngredient[];
  selectedUserListIngredient: string;
  measureUnits: IMeasurementUnit[];
}

export namespace IngredientsAction {

  export class Get {
    static readonly type: string = '[Ingredient] Get Ingredients Action';
    // antes del cambio a base de datos de vectores
    // constructor(public filters?: any, public appendData = false, public search = '') { }
    constructor(public q?: string, public categoryId?: string) {
    }
  }

  export class GetById {
    static readonly type: string = '[Ingredient] Get Ingredient By Id Action';
    constructor(public id: string, public filters: any = void 0, public appendData = false) { }
  }

  export class Set {
    static readonly type: string = '[Ingredient] Set Ingredients Action';
    constructor(public ingredient: IIngredient, public appendData = false) { }
  }

  export class GetIngredientListById {
    static readonly type: string = '[Ingredient] Get Ingredient List By Id Action';
    constructor(public ids: string[], public filters: any = void 0, public search = '') { }
  }

  export class GetCategories {
    static readonly type: string = '[Ingredient] Get Ingredient Categories Action';
    constructor(public filters: any = void 0) { }
  }

  export class AddUserIngredientToIngredients {
    static readonly type: string = '[Ingredient] Add User Ingredient To Ingredients Action';
    constructor() { }
  }

  export class AddSelectedIngredient {
    static readonly type: string = '[Ingredient] Add Selected Ingredient Action';
    constructor(public ingredient: IIngredient) { }
  }

  export class ResetIngredients {
    static readonly type: string = '[Ingredient] Reset Ingredients Action';
    constructor() { }
  }

  export class SetSelectedIdUserListIngredient {
    static readonly type: string = '[Ingredient] Set Selected User List Id Ingredient Action';
    constructor(public id: string) { }
  }

  export class GetMeasureUnits {
    static readonly type: string = '[Food Group] Get Measure Units Action';
    constructor(public filters?: any) { }
  }

  export class RemoveSelectedUserListIngredient {
    static readonly type: string = '[Ingredient] Remove Selected User List Ingredient To Ingredients Action Action';
    constructor() { }
  }
}

@State<IRIngredient>({
  name: 'IngredientState',
  defaults: {
    ingredients: [],
    ingredientsSearch: null,
    selectedId: null,
    ingredientCategories: [],
    count: 0,
    userListIngredients: [],
    selectedUserListIngredient: null,
    measureUnits: [],
  }
})
@Injectable()
export class IngredientState {
  constructor(
    private baseService: BaseService<any>
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getIngredients(state: IRIngredient): IIngredient[] {
    return state.ingredients;
  }

  @Selector()
  static getSelectedIngredient(state: IRIngredient): IIngredient {
    return state.ingredients.find((ingredient: IIngredient) => ingredient._id === state.selectedId);
  }

  @Selector()
  public static getIngredientCategories(state: IRIngredient): IIngredientCategory[] {
    return state.ingredientCategories;
  }



  @Selector()
  public static getUserListIngredients(state: IRIngredient): IIngredient[] {
    return state.userListIngredients;
  }

  @Selector()
  static getSelectedUserListIngredient(state: IRIngredient): IIngredient {
    return state.userListIngredients.find((ingredient: IIngredient) => ingredient._id === state.selectedUserListIngredient);
  }

  @Selector()
  public static getMeasureUnits(state: IRIngredient): IMeasurementUnit[] {
    return state.measureUnits;
  }

  @Action(IngredientsAction.Get)
  async getIngredientsAction(ctx: StateContext<IRIngredient>, {q, categoryId}: IngredientsAction.Get): Promise<void> {
    if (!q) return;
    const url = `${this.url}/ingredients/search`;
    const response: DataBaseServiceResponse<IIngredientSearch | IIngredient[]> = await firstValueFrom(
      this.baseService.get(`${url}`, void 0, void 0, {
        params: {
          q,
          categoryId,
        }
      }));
    if (response.error || !response.entity) throw new Error(response.message);

    // asi estaba antes del cambio a la base de datos de vector
    // const ingredients: IIngredient[] = !action.appendData ? response.entity : [...(state.ingredients ?? []), ...(response.entity ?? [])];
    ctx.patchState({
      ingredients: (response.entity as IIngredientSearch).hits.map((hit: IIngredientSearchHit) => hit.document)
    });
  }

  @Action(IngredientsAction.GetById)
  async getIngredientByIdAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.GetById): Promise<void> {
    const state: IRIngredient = cloneDeep(ctx.getState());
    const response: DataBaseServiceResponse<IIngredient> = await firstValueFrom(
      (this.baseService.get(`${this.url}/ingredients/${action.id}`, action.filters ?? void 0))
    );
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      ingredients: !action.appendData ? [response.entity] : [response.entity, ...state.ingredients],
      selectedId: action.id
    });
  }

  @Action(IngredientsAction.Set)
  async setIngredientAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.Set): Promise<void> {
    const state: IRIngredient = cloneDeep(ctx.getState());
    ctx.patchState({
      ingredients: !action.appendData ? [action.ingredient] : [action.ingredient, ...state.ingredients],
      selectedId: action.ingredient._id
    });
  }

  @Action(IngredientsAction.GetIngredientListById)
  async getIngredientListByIdAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.GetIngredientListById): Promise<void> {
    let filters: ServiceLoopbackFilters = action.filters;
    if (!filters) {
      filters = {};
    }
    filters.where = {
      or: []
    };
    for (const id of action.ids) {
      filters.where.or.push({
        _id: id
      });
    }
    const response: DataBaseServiceResponse<IIngredient[]> = await firstValueFrom(this.baseService.get(`${this.url}/ingredients`, filters, void 0, {
      params: {
        search: action.search
      }
    }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      userListIngredients: response.entity
    });
  }

  @Action(IngredientsAction.GetCategories)
  async getIngredientCategoriesAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.GetCategories): Promise<void> {
    const response: DataBaseServiceResponse<IIngredientCategory[]> = await firstValueFrom(
      (this.baseService.get(`${this.url}/ingredients/categories`, action.filters ?? void 0))
    );
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      ingredientCategories: response.entity
    });
  }

  @Action(IngredientsAction.AddSelectedIngredient)
  async addSelectedIngredientAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.AddSelectedIngredient): Promise<void> {
    const state: IRIngredient = cloneDeep(ctx.getState());
    const ingredients: IIngredient[] = state.userListIngredients;
    const index: number = ingredients.findIndex(i => i._id === action.ingredient._id);
    if (index === -1) {
      ctx.patchState({
        userListIngredients: [...ingredients, action.ingredient]
      });
    }
  }

  @Action(IngredientsAction.SetSelectedIdUserListIngredient)
  async setSelectedIdUserListIngredientAction(
    ctx: StateContext<IRIngredient>, action?: IngredientsAction.SetSelectedIdUserListIngredient): Promise<void> {
    ctx.patchState({
      selectedUserListIngredient: action.id,
    });
  }

  @Action(IngredientsAction.AddUserIngredientToIngredients)
  async addSelectedUserListIngredientToIngredientsAction(ctx: StateContext<IRIngredient>): Promise<void> {
    const state: IRIngredient = cloneDeep(ctx.getState());
    const ingredient: IIngredient = state.userListIngredients.find(i => i._id === state.selectedUserListIngredient);
    const index: number = state.ingredients.findIndex(i => i._id === ingredient._id);
    if (index === -1) {
      ctx.patchState({
        ingredients: [ingredient, ...state.ingredients],
      });
    }
  }

  @Action(IngredientsAction.RemoveSelectedUserListIngredient)
  async removeSelectedUserListIngredientToIngredientsAction(ctx: StateContext<IRIngredient>): Promise<void> {
    const state: IRIngredient = cloneDeep(ctx.getState());
    const ingredients: IIngredient[] = state.ingredients;
    ingredients.splice(0, 1);
    ctx.patchState({
      ingredients,
    });
  }

  @Action(IngredientsAction.ResetIngredients)
  async resetIngredientsAction(ctx: StateContext<IRIngredient>): Promise<void> {
    ctx.patchState({
      ingredients: [],
    });
  }

  @Action(IngredientsAction.GetMeasureUnits)
  async getMeasureUnitsAction(ctx: StateContext<IRIngredient>, action?: IngredientsAction.GetMeasureUnits): Promise<void> {
    const response: DataBaseServiceResponse<IMeasurementUnit[]> = await firstValueFrom(
      (this.baseService.get(`${this.url}/ingredients/measureUnits`, void 0))
    );
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      measureUnits: Array.isArray(response.entity) ? response.entity : [response.entity],
    });
  }

}
